// searchSentence.js — ONE semantic representation of a search (SEARCH_GRAMMAR §1, Stage 4).
//
// The grammar's sentence, as data:
//
//   Object → Intent → Identity(Category→Kind→Subtype[→Status]) → Location → Budget → Size
//          → Characteristics → Operational
//
// 🔒 THE SENTENCE IS A PROJECTION, NOT A SECOND STATE (the §5b law, applied again).
// Canonical state stays the flat predicate fields (kinds/subtypes/priceMin/…) that the URL,
// the API, saved searches and every component already write. `toSentence()` derives the
// grammar view FROM those fields, exactly as the tree derives its lit node — so the compiler,
// the box, the panel, the tree, saved searches and the URL share one semantic representation
// WITHOUT a new wire format, a migration, or a second source of truth. If the sentence were
// stored, it would immediately start disagreeing with the fields it summarises.
//
// Consumers today: compileQuery() attaches it to every compilation (the owner's Stage-4
// example: "New apartment for sale in Saburtalo under $150k" is a sentence, not five
// unrelated patches); telemetry can log it; renderers can walk it. Consumers tomorrow: the
// AI compiler emits it, saved-search cards display it. All of them call THIS function.

import { deriveIdentityPath } from './taxonomy.js'
import { APARTMENT_STATUS, APARTMENT_CONTEXT_CODES } from './taxonomy.js'

const num = v => (v === '' || v == null ? null : Number(v))
const arr = v => (Array.isArray(v) && v.length ? [...v] : null)

// filters: the flat advFilters/committed-filters shape. opts.objectType: the Desk object
// ('Property' | 'Leads' | …) — it lives OUTSIDE the filters object today (entryTypeFilter),
// so the caller supplies it; defaulted, never guessed from filter contents.
export function toSentence(filters = {}, { objectType = 'Property' } = {}) {
  const f = filters
  const identity = deriveIdentityPath(f)

  const sizeEntries = {
    rooms: arr(f.roomsSet),
    bedrooms: arr(f.bedroomsSet),
    bathrooms: [num(f.bathroomsMin), num(f.bathroomsMax)],
    floor: [num(f.floorMin), num(f.floorMax)],
    floorsTotal: [num(f.floorsTotalMin), num(f.floorsTotalMax)],
    topFloor: f.topFloor === true ? true : null,
    area: [num(f.areaMin), num(f.areaMax)],
    landArea: [num(f.landAreaMin), num(f.landAreaMax)],
  }
  const size = {}
  for (const [k, v] of Object.entries(sizeEntries)) {
    if (v == null) continue
    if (Array.isArray(v)) { if (v.some(x => x != null)) size[k] = v } else size[k] = v
  }

  const priceMin = num(f.priceMin), priceMax = num(f.priceMax)
  // Status belongs to Identity (the apartment axis); everything else in featureCodes is a
  // Characteristic. One list, two grammar slots — split here, once.
  // The apart-hotel context (core 1940) is identity too, but only when the tree can name it
  // (an apartment search) — otherwise it stays an ordinary characteristic.
  const amenities = (f.featureCodes || []).filter(c => !APARTMENT_STATUS.includes(c)
    && !(identity.context && APARTMENT_CONTEXT_CODES.includes(c)))

  const operationalEntries = {
    seen: f.seen || null, claimsLt: num(f.claimsLt), claimsGt: num(f.claimsGt),
    dateField: (f.dateFrom || f.dateTo) ? (f.dateField || 'added') : null,
    dateFrom: f.dateFrom || null, dateTo: f.dateTo || null,
    sources: arr(f.sources),
  }
  const operational = {}
  for (const [k, v] of Object.entries(operationalEntries)) if (v != null) operational[k] = v

  return {
    object: objectType,
    // multi-intent is a legal search; a single one is the sentence's Intent slot, the array
    // is kept beside it so nothing is lost in projection.
    intent: identity.intent,
    intents: arr(f.intents),
    identity: {
      category: identity.category,
      kind: identity.kind,
      subtype: identity.subtype,
      status: identity.status,
      context: identity.context,
    },
    location: {
      districtCodes: arr(f.districtCodes),
      near: f.near && f.near.lat != null ? { ...f.near } : null,
    },
    budget: (priceMin != null || priceMax != null)
      ? { min: priceMin, max: priceMax, currency: f.currency || null }
      : null,
    size: Object.keys(size).length ? size : null,
    // 0433: `condition` is its own slot, not folded into `features`. They compose
    // differently (OR vs AND) and mean different things — "the state of the flat" is a
    // Characteristic in its own right, and merging them here would make the projection
    // claim a conjunction the search never asked for.
    characteristics: (amenities.length || (f.restrictions || []).length || (f.conditionCodes || []).length)
      ? {
        features: amenities.length ? amenities : null,
        condition: arr(f.conditionCodes),
        restrictions: arr(f.restrictions),
      }
      : null,
    operational: Object.keys(operational).length ? operational : null,
    // free text that compiled to nothing stays visible in the sentence — the residual is a
    // real part of what the agent asked, not noise to drop.
    text: (f.q || '').trim() || null,
  }
}

// The sentence, one line, for logs and debugging — NOT a UI string (renderers own display).
export function sentenceLine(s) {
  if (!s) return ''
  const idn = [s.identity?.category, s.identity?.kind, s.identity?.subtype, s.identity?.status]
    .filter(Boolean).join('→')
  const parts = [
    s.object,
    s.intent || (s.intents ? s.intents.join('|') : null),
    idn || null,
    s.location?.districtCodes ? `@${s.location.districtCodes.join(',')}` : null,
    s.location?.near ? `near(${s.location.near.id || `${s.location.near.lat},${s.location.near.lng}`})` : null,
    s.budget ? `${s.budget.min ?? ''}…${s.budget.max ?? ''}${s.budget.currency ? ' ' + s.budget.currency : ''}` : null,
    s.size ? Object.entries(s.size).map(([k, v]) => `${k}:${Array.isArray(v) ? v.filter(x => x != null).join('-') : v}`).join(' ') : null,
    s.characteristics?.features ? `+${s.characteristics.features.join('+')}` : null,
    s.text ? `“${s.text}”` : null,
  ].filter(Boolean)
  return parts.join(' · ')
}
